/**
 * Intent Classification — Parse LLM responses and extract intent category + candidate actions.
 *
 * Handles both well-formed and malformed LLM JSON responses, providing graceful
 * degradation when the response cannot be parsed.
 *
 * @module gibbs/text-to-action/classifyIntent
 */

/** @type {readonly string[]} */
const VALID_INTENTS = Object.freeze(['single_action', 'workflow', 'clarify', 'chat', 'modify_workflow']);

/**
 * @typedef {Object} ClassifiedIntent
 * @property {'single_action' | 'workflow' | 'clarify' | 'chat' | 'modify_workflow'} intent
 * @property {Array} actions - Action entries with actionId, params,
 *   resolvedInputs, step, inputsFrom
 * @property {string} [question] - For clarify intent
 * @property {Array<{label: string, value: *}>} [options] - For clarify intent
 * @property {string[]} [missing] - Missing params for clarify intent
 * @property {Object} [context] - Additional context (e.g., {actionId} for clarify)
 * @property {string} [summary] - Workflow summary
 * @property {string} [message] - Chat message or error message
 * @property {boolean} raw - Whether the result was parsed from raw text (fallback)
 */

/**
 * Parse an LLM response string into a classified intent.
 *
 * Attempts JSON.parse first. On failure, applies regex-based extraction as a
 * fallback. Returns a `chat` intent with an error message if all parsing fails.
 *
 * @param {string} llmResponse - Raw string from the LLM
 * @returns {ClassifiedIntent}
 */
export function classifyIntent(llmResponse) {
    if (typeof llmResponse !== 'string' || llmResponse.trim() === '') {
        return fallback('Received empty response from LLM.');
    }

    // Try JSON parse
    const parsed = tryParseJSON(llmResponse);
    if (parsed !== null) {
        return normalizeResponse(parsed);
    }

    // Fallback: try to extract JSON from markdown code blocks or partial JSON
    const extracted = extractJSONFromText(llmResponse);
    if (extracted !== null) {
        const result = normalizeResponse(extracted);
        result.raw = true;
        return result;
    }

    // Last resort: regex extraction of actionIds
    const regexResult = extractFromRegex(llmResponse);
    if (regexResult) {
        return regexResult;
    }

    return fallback('Could not parse LLM response. Please try rephrasing your request.');
}

/**
 * Validate that a classified intent has the expected shape for its type.
 *
 * @param {ClassifiedIntent} classified
 * @returns {{valid: boolean, warnings: string[]}}
 */
export function validateClassifiedIntent(classified) {
    const warnings = [];

    if (!VALID_INTENTS.includes(classified.intent)) {
        warnings.push(`Unknown intent type: "${classified.intent}"`);
    }

    if (classified.intent === 'single_action') {
        if (!Array.isArray(classified.actions) || classified.actions.length !== 1) {
            warnings.push('single_action intent should have exactly one action');
        }
        if (classified.actions?.some((a) => !a.actionId)) {
            warnings.push('Action missing actionId');
        }
    }

    if (classified.intent === 'workflow') {
        if (!Array.isArray(classified.actions) || classified.actions.length < 2) {
            warnings.push('workflow intent should have at least two actions');
        }
        if (classified.actions?.some((a) => !a.actionId)) {
            warnings.push('Action missing actionId');
        }
    }

    if (classified.intent === 'clarify') {
        if (!classified.question) {
            warnings.push('clarify intent should have a question');
        }
    }

    if (classified.intent === 'modify_workflow') {
        if (!Array.isArray(classified.modifications) || classified.modifications.length === 0) {
            warnings.push('modify_workflow intent should have a non-empty modifications array');
        }
    }

    return { valid: warnings.length === 0, warnings };
}

// ─── Internal Helpers ───────────────────────────────────────────────────────

/**
 * Attempt JSON.parse, returning null on failure.
 * @param {string} str
 * @returns {Object|null}
 */
function tryParseJSON(str) {
    try {
        const result = JSON.parse(str.trim());
        return typeof result === 'object' && result !== null ? result : null;
    } catch {
        return null;
    }
}

/**
 * Try to extract a JSON object from text that may contain markdown fences or
 * surrounding prose.
 * @param {string} text
 * @returns {Object|null}
 */
function extractJSONFromText(text) {
    // Try markdown code block first
    const codeBlockMatch = text.match(/```(?:json)?\s*\n?([\s\S]*?)\n?```/);
    if (codeBlockMatch) {
        const parsed = tryParseJSON(codeBlockMatch[1]);
        if (parsed) return parsed;
    }

    // Try to find a JSON object in the text (first { to last })
    const firstBrace = text.indexOf('{');
    const lastBrace = text.lastIndexOf('}');
    if (firstBrace !== -1 && lastBrace > firstBrace) {
        const candidate = text.slice(firstBrace, lastBrace + 1);
        const parsed = tryParseJSON(candidate);
        if (parsed) return parsed;
    }

    return null;
}

/**
 * Regex-based fallback: extract actionIds from text like "actionId": "dock-compound".
 * @param {string} text
 * @returns {ClassifiedIntent|null}
 */
function extractFromRegex(text) {
    const actionIdMatches = [...text.matchAll(/["']?actionId["']?\s*:\s*["']([a-z0-9-]+)["']/g)];
    if (actionIdMatches.length > 0) {
        const actions = actionIdMatches.map((m) => ({ actionId: m[1] }));
        return {
            intent: actions.length === 1 ? 'single_action' : 'workflow',
            actions,
            raw: true,
            message: 'Parsed from malformed response via regex extraction.',
        };
    }
    return null;
}

/**
 * Normalize a parsed JSON object into the ClassifiedIntent shape.
 * @param {Object} obj
 * @returns {ClassifiedIntent}
 */
function normalizeResponse(obj) {
    const intent = VALID_INTENTS.includes(obj.intent) ? obj.intent : inferIntent(obj);
    const actions = normalizeActions(obj.actions);

    const result = {
        intent,
        actions,
        raw: false,
    };

    // Copy through optional fields
    if (obj.question) result.question = obj.question;
    if (Array.isArray(obj.options)) result.options = obj.options;
    if (Array.isArray(obj.missing)) result.missing = obj.missing;
    if (obj.context) result.context = obj.context;
    if (obj.summary) result.summary = obj.summary;
    if (obj.message) result.message = obj.message;
    if (Array.isArray(obj.modifications)) result.modifications = obj.modifications;

    return result;
}

/**
 * Infer intent from response shape when explicit intent field is missing/invalid.
 * @param {Object} obj
 * @returns {string}
 */
function inferIntent(obj) {
    if (Array.isArray(obj.modifications)) return 'modify_workflow';
    if (obj.question || obj.missing) return 'clarify';
    if (Array.isArray(obj.actions)) {
        return obj.actions.length > 1 ? 'workflow' : 'single_action';
    }
    if (obj.actionId) return 'single_action';
    return 'chat';
}

/**
 * Normalize the actions array from various LLM response shapes.
 * @param {*} actions
 * @returns {Array<{actionId: string, params?: Object}>}
 */
function normalizeActions(actions) {
    if (!Array.isArray(actions)) return [];
    return actions
        .map((a) => {
            if (typeof a === 'string') return { actionId: a };
            if (typeof a === 'object' && a !== null && a.actionId) return a;
            return null;
        })
        .filter(Boolean);
}

/**
 * Produce a fallback chat intent with an error message.
 * @param {string} message
 * @returns {ClassifiedIntent}
 */
function fallback(message) {
    return {
        intent: 'chat',
        actions: [],
        message,
        raw: true,
    };
}
